import { useDateRangeContext } from '@context'
import { FloatingFocusManager, type FloatingContext, type Strategy } from '@floating-ui/react'
import { IDateRangeKey } from '@query'
import cn from 'classnames'
import dayjs from 'dayjs'
import { AnimatePresence } from 'framer-motion'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { DateRangeCalendar, DateRangeEdge } from './DateRangeCalendar'
import {
  CustomBlock,
  CustomField,
  CustomFieldLabel,
  CustomFields,
  CustomFieldValue,
  Footer,
  FooterClear,
  FooterDates,
  FooterDone,
  StyledMenu
} from './DateRangeDropdown.styled'
import { DateRangePresets, PRESET_GROUPS } from './DateRangePresets'
import { formatDateRangeDay, parseDateRangeDay } from './dateRangeDay'

interface DateRangeMenuProps {
  context: FloatingContext
  isVisible: boolean
  onClose: () => void
  x?: number
  y?: number
  strategy?: Strategy
}

export const DateRangeDropdown = React.forwardRef<HTMLElement, DateRangeMenuProps>(
  ({ isVisible, context, onClose, x = 0, y = 0, strategy }, ref) => {
    const [t] = useTranslation()
    const { selectedRange: selected, options, setRange, reset } = useDateRangeContext()
    const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))

    const [isPickingCustom, setPickingCustom] = useState(false)
    const isCustom = selected?.key === 'custom' || isPickingCustom
    const [isCalendarOpen, setCalendarOpen] = useState(false)
    const [edge, setEdge] = useState<DateRangeEdge>('from')
    const from = selected?.date_from
    const to = selected?.date_to
    const [draftFrom, setDraftFrom] = useState(from)
    const [draftTo, setDraftTo] = useState(to)
    const [typedFrom, setTypedFrom] = useState(formatDateRangeDay(from))
    const [typedTo, setTypedTo] = useState(formatDateRangeDay(to))
    const [editing, setEditing] = useState<DateRangeEdge | null>(null)
    const [month, setMonth] = useState(() =>
      dayjs(selected?.date_from ?? undefined).startOf('month')
    )
    const canApplyCustom = Boolean(draftFrom && draftTo)

    useEffect(() => {
      setDraftFrom(from)
      setDraftTo(to)
    }, [from, to])

    useEffect(() => {
      if (editing !== 'from') setTypedFrom(formatDateRangeDay(draftFrom))
      if (editing !== 'to') setTypedTo(formatDateRangeDay(draftTo))
    }, [draftFrom, draftTo, editing])

    // Reopening on a preset should not leave the calendar hanging open.
    useEffect(() => {
      if (!isVisible) {
        setCalendarOpen(false)
        setPickingCustom(false)
        setEditing(null)
      }
    }, [isVisible])

    useEffect(() => {
      const anchor = edge === 'to' ? draftTo ?? draftFrom : draftFrom ?? draftTo
      if (anchor) setMonth(dayjs(anchor).startOf('month'))
    }, [selected?.key, draftFrom, draftTo, edge, isVisible])

    const byKey = useMemo(() => new Map(options.map(option => [option.key, option])), [options])
    const grouped = useMemo(() => {
      const claimed = new Set(PRESET_GROUPS.flatMap(group => group.keys))
      const leftovers = options
        .map(option => option.key)
        .filter(key => key !== 'custom' && !claimed.has(key))

      return PRESET_GROUPS.map(group => ({
        ...group,
        keys: [...group.keys, ...(group.hasCustom ? leftovers : [])].filter(key => byKey.has(key))
      })).filter(group => group.keys.length > 0 || group.hasCustom)
    }, [options, byKey])

    const pickPreset = (key: IDateRangeKey) => {
      const option = byKey.get(key)
      if (option) setRange(option)
      setCalendarOpen(false)
      setPickingCustom(false)
      onClose()
    }

    const pickCustom = () => {
      setEdge('from')
      setPickingCustom(true)
      setCalendarOpen(current => !(isCustom && current))
      if (from && to) {
        setRange({ key: 'custom', date_from: from, date_to: to })
      }
    }

    const commitCustom = (nextFrom?: string, nextTo?: string) => {
      setDraftFrom(nextFrom)
      setDraftTo(nextTo)
      if (nextFrom && nextTo) {
        setRange({ key: 'custom', date_from: nextFrom, date_to: nextTo })
      }
    }

    const applyEdgeIso = (field: DateRangeEdge, iso: string) => {
      if (field === 'from') {
        commitCustom(iso, draftTo && iso > draftTo ? iso : draftTo)
        return
      }
      commitCustom(draftFrom, draftFrom && iso < draftFrom ? draftFrom : iso)
    }

    const pickDay = (value: string) => {
      applyEdgeIso(edge, value)
      if (edge === 'from') {
        setEdge('to')
        return
      }
      setEdge('from')
      setCalendarOpen(false)
    }

    const onTypedDate = (field: DateRangeEdge, value: string) => {
      if (field === 'from') setTypedFrom(value)
      else setTypedTo(value)
      const iso = parseDateRangeDay(value)
      if (iso) applyEdgeIso(field, iso)
    }

    const onTypedDateBlur = (field: DateRangeEdge, value: string) => {
      const trimmed = value.trim()
      if (!trimmed) {
        if (field === 'from') commitCustom(undefined, draftTo)
        else commitCustom(draftFrom, undefined)
      } else {
        const iso = parseDateRangeDay(trimmed)
        if (iso) applyEdgeIso(field, iso)
      }
      setEditing(null)
    }

    const selectedDates =
      draftFrom && draftTo
        ? `${formatDateRangeDay(draftFrom)} – ${formatDateRangeDay(draftTo)}`
        : t('components.dateRangeFilter.noDateLimit')

    return createPortal(
      <AnimatePresence>
        {isVisible && (
          <FloatingFocusManager context={context}>
            <StyledMenu
              ref={ref as any}
              className="date-range-options"
              data-test="date-range-options"
              style={{ top: y || 0, left: x || 0, position: strategy }}
            >
              <DateRangePresets
                groups={grouped}
                selectedKey={selected?.key}
                isCustom={isCustom}
                onPreset={pickPreset}
                onCustom={pickCustom}
              />

              {isCustom && (
                <CustomBlock>
                  <CustomFields>
                    {(['from', 'to'] as DateRangeEdge[]).map(field => (
                      <CustomField
                        key={field}
                        className={cn({
                          isActive: edge === field && (isCalendarOpen || editing === field)
                        })}
                      >
                        <CustomFieldLabel>
                          {t(
                            field === 'from'
                              ? 'components.dateRangeFilter.startDate'
                              : 'components.dateRangeFilter.endDate'
                          )}
                        </CustomFieldLabel>
                        <CustomFieldValue
                          type="text"
                          autoComplete="off"
                          spellCheck={false}
                          placeholder="—"
                          value={field === 'from' ? typedFrom : typedTo}
                          onFocus={() => {
                            setEdge(field)
                            setEditing(field)
                            setCalendarOpen(true)
                          }}
                          onChange={event => onTypedDate(field, event.currentTarget.value)}
                          onBlur={event => onTypedDateBlur(field, event.currentTarget.value)}
                          onKeyDown={event => {
                            if (event.key !== 'Enter') return
                            event.preventDefault()
                            event.currentTarget.blur()
                          }}
                        />
                      </CustomField>
                    ))}
                  </CustomFields>

                  {isCalendarOpen && (
                    <DateRangeCalendar
                      month={month}
                      onMonthChange={setMonth}
                      edge={edge}
                      from={draftFrom}
                      to={draftTo}
                      onPickDay={pickDay}
                    />
                  )}
                </CustomBlock>
              )}

              <Footer>
                <FooterDates>{selectedDates}</FooterDates>
                <FooterClear
                  type="button"
                  onClick={() => {
                    reset()
                    setCalendarOpen(false)
                    setPickingCustom(false)
                    setDraftFrom(undefined)
                    setDraftTo(undefined)
                    setTypedFrom('')
                    setTypedTo('')
                    setEditing(null)
                  }}
                >
                  {t('components.dateRangeFilter.clear')}
                </FooterClear>
                <FooterDone
                  type="button"
                  disabled={isCustom && !canApplyCustom}
                  onClick={() => {
                    if (isCustom && !canApplyCustom) return
                    onClose()
                  }}
                >
                  {t('components.dateRangeFilter.done')}
                </FooterDone>
              </Footer>
            </StyledMenu>
          </FloatingFocusManager>
        )}
      </AnimatePresence>,
      rootRef.current
    )
  }
)
